import moment from 'moment-timezone';
import { useObserver } from 'mobx-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { removeTypename } from 'shared/helpers/utils';
import { GetDutchiePosLoyaltyConfigDocument } from 'src/gql/graphql';
import type {
  DutchiePosLoyaltyConfig,
  GetDutchiePosLoyaltyConfigQuery,
  RewardsActivity,
  RewardsListV2,
  RewardV2,
} from 'src/gql/graphql';
import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import {
  LoyaltyExpirationMode,
  RedemptionType,
  TierEarningBasis,
  formatLoyaltyExpirationDate,
  toUtcString,
} from 'src/components/loyalty/rewards/dpos-utils';

export type UseLoyaltyReturn = {
  loyaltyConfig: GetDutchiePosLoyaltyConfigQuery['getDutchiePosLoyaltyConfig'];
  loading: boolean;
  formatValueForDisplay: (value: number, label?: string, isLoyaltyPoints?: boolean) => string;
  formatValueForDaysForExpire: (daysToExpiration: number) => string;
  getExpiresVerb: (value: number) => string;
  getExpiryUnit: (value: number) => string;
  formatExpirationDate: (isoDate: string) => string;
  rewardsProgramName: string;
  balanceDaysToExpiration: number | null | undefined;
  badgeColor: string;
  currentTierName: string | undefined;
  currentTierThreshold: number | undefined;
  nextTierName: string | undefined;
  currentBalance: number;
  currentYearTierBalance: number;
  expirationDate: string | undefined;
  soonestExpiringBalance: number | null;
  soonestExpirationDate: string | null;
  targetBalance: number;
  targetRewardsBalance: number;
  progressPercentageToNextTier: string;
  progressPercentageToNextReward: string;
  progressToNextReward: number;
  currentRewardsBalance: number;
  isLoyaltyMember: boolean;
  isPoints: boolean;
  hasTiers: boolean;
  redemptionType: string;
  isSingleTier: boolean;
  isDiscountRedemption: boolean;
  posCustomerId: string | undefined;
  filteredRewardsActivity?: Array<RewardsActivity>;
  loyaltyEnterpriseConfigData?: DutchiePosLoyaltyConfig | undefined;
  rewardsEnterpriseData?: RewardsListV2 | undefined;
  loyaltyWidgetEnabled?: boolean;
  rewardsArray: Array<RewardV2>;
  showDiscountRewardsOnly: boolean;
  isRewardApplyBlocked: boolean;
  showRewardsBlockOnWeb: boolean;
  accrualRate: number | null;
  isExpirationDisplayEnabled: boolean;
};

export type UseLoyaltyOptions = {
  skip?: boolean;
  /**
   * Allows loyalty queries to run on plain (non-Pro) Kiosk. Kiosk menu/home screens
   * intentionally hide loyalty since they're shared, pre-authentication surfaces, but
   * checkout happens after the customer has entered their phone/PIN, so it's safe to
   * show their balance and let them redeem there. See POS-20008.
   */
  allowOnKiosk?: boolean;
};

export type ShowRewardsBlockOnWebInput = {
  isAlpineIQEnabled: boolean | undefined;
  isSpringbigEnabled: boolean | undefined;
  isDutchiePOSLoyaltyEnabled: boolean | undefined;
  loyaltyConfig: DutchiePosLoyaltyConfig | null | undefined;
};

export type ShouldShowLoyaltyWidgetInput = {
  loyaltyConfig: DutchiePosLoyaltyConfig | null | undefined;
  isLoyaltyMember: boolean;
};

/** Pure visibility rule for the loyalty widget itself, gated on `loyaltyWidgetEnabledOnWeb`. */
export function getShouldShowLoyaltyWidget({ loyaltyConfig, isLoyaltyMember }: ShouldShowLoyaltyWidgetInput): boolean {
  return !!(
    loyaltyConfig?.isEnabled &&
    (loyaltyConfig.loyaltyWidgetEnabledOnWeb ?? true) &&
    ((isLoyaltyMember && loyaltyConfig.loyaltyRequireOptIn) || !loyaltyConfig.loyaltyRequireOptIn)
  );
}

/** Pure visibility rule for the checkout rewards block (Alpine/SpringBig vs DPOS + loaded config with onlineRedemptionEnabledOnWeb). */
export function getShowRewardsBlockOnWeb({
  isAlpineIQEnabled,
  isSpringbigEnabled,
  isDutchiePOSLoyaltyEnabled,
  loyaltyConfig,
}: ShowRewardsBlockOnWebInput): boolean {
  return (
    !!isAlpineIQEnabled ||
    !!isSpringbigEnabled ||
    (!!isDutchiePOSLoyaltyEnabled && loyaltyConfig != null && !!loyaltyConfig.onlineRedemptionEnabledOnWeb)
  );
}

/** Shared skip + integration flags for Dutchie POS loyalty queries (config + rewards). */
function useLoyaltyQueryPrerequisites(options?: UseLoyaltyOptions) {
  const { isKiosk } = useUI();
  const isKioskProEnabled = useKioskPro();
  const User = useUser();
  const isUserLoading = useObserver(() => User.loading);
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const phone = useObserver(() => User.phone);
  const isUserLoadedAndLoggedIn = !isUserLoading && isLoggedIn;

  const { dispensary } = useDispensary();
  const isDutchiePOSLoyaltyEnabled = dispensary?.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled;
  const isAlpineIQEnabled = dispensary?.consumerDispensaryIntegrations?.alpineiq?.enabled;
  const isSpringbigEnabled = dispensary?.consumerDispensaryIntegrations?.springbig?.enabled;

  const skip =
    options?.skip === true
      ? true
      : !isUserLoadedAndLoggedIn ||
        !isDutchiePOSLoyaltyEnabled ||
        !phone ||
        (isKiosk && !isKioskProEnabled && !options?.allowOnKiosk);

  return {
    skip,
    dispensary,
    isDutchiePOSLoyaltyEnabled,
    isAlpineIQEnabled,
    isSpringbigEnabled,
    phone,
    User,
  };
}

/**
 * Lightweight visibility for the checkout rewards section: same rules as `useLoyalty().showRewardsBlockOnWeb`
 * without `useGetRewards` or tier/reward calculations. Uses the same loyalty config query + Apollo cache as `useLoyalty`.
 */
export function useShowRewardsBlockOnWeb(options?: UseLoyaltyOptions): boolean {
  const {
    skip,
    dispensary,
    isAlpineIQEnabled,
    isSpringbigEnabled,
    isDutchiePOSLoyaltyEnabled,
  } = useLoyaltyQueryPrerequisites(options);

  const { data: loyaltyConfigData } = useQuery(GetDutchiePosLoyaltyConfigDocument, {
    variables: {
      dispensaryId: dispensary?.id,
    },
    skip,
  });

  const loyaltyConfig = loyaltyConfigData?.getDutchiePosLoyaltyConfig;

  return getShowRewardsBlockOnWeb({
    isAlpineIQEnabled,
    isSpringbigEnabled,
    isDutchiePOSLoyaltyEnabled,
    loyaltyConfig,
  });
}

export const useLoyalty = (
  activeEnterpriseId: string | undefined = undefined,
  loyaltyEnterpriseConfigData: DutchiePosLoyaltyConfig | undefined = undefined,
  rewardsEnterpriseData: RewardsListV2 | undefined = undefined,
  options?: UseLoyaltyOptions
): UseLoyaltyReturn => {
  const flags = useFlags();
  const isSkipSMSSendingEnabled = flags['pos.customers.skip-sms-sending.rollout'] ?? false;
  const isLoyaltySoonestExpiringBalanceEnabled =
    flags['pos.customers.loyalty-soonest-expiring-balance.rollout'] ?? false;
  // Same flag used in checkout/components/sections/rewards/index.jsx (ER-5628). Dark launch: defaults
  // off so keeping view-only rewards visible (rather than filtering them out) only takes effect once
  // explicitly rolled out.
  const isGateOnlineRedemptionWidgetEnabled = flags['pos.ecomm.gate-online-redemption-widget.rollout'] ?? false;

  const {
    skip,
    dispensary,
    isDutchiePOSLoyaltyEnabled,
    isAlpineIQEnabled,
    isSpringbigEnabled,
    phone,
    User,
  } = useLoyaltyQueryPrerequisites(options);
  const birthday = useObserver(() => User.birthday);

  const { data: loyaltyConfigData, loading: loyaltyConfigLoading } = useQuery(GetDutchiePosLoyaltyConfigDocument, {
    variables: {
      dispensaryId: dispensary?.id,
    },
    skip: !!loyaltyEnterpriseConfigData || skip,
  });

  const { data: rewardsData, loading: rewardsLoading } = useGetRewards({
    dispensaryId: dispensary?.id,
    birthday,
    phone,
    pin: isSkipSMSSendingEnabled ? 'skip' : '',
    customerId: User?.id,
    skip: !!rewardsEnterpriseData || skip,
  });

  const loyaltyConfig = activeEnterpriseId
    ? loyaltyEnterpriseConfigData
    : loyaltyConfigData?.getDutchiePosLoyaltyConfig;
  const rewards = activeEnterpriseId ? rewardsEnterpriseData : rewardsData?.getRewardsV2;
  // `rewards.rewards` is typed `Array<Maybe<RewardV2>>` - the list itself is non-null but its
  // elements are nullable, so both branches must drop null/undefined elements before anything
  // downstream (e.g. DposApplyRewardsCheckout) destructures them. Flag ON keeps view-only rewards
  // visible (disabled downstream); flag OFF restores the legacy isRedeemableOnline-only behavior.
  const rewardsArray = rewards?.rewards
    .filter((reward): reward is RewardV2 => reward != null)
    .filter((reward) => isGateOnlineRedemptionWidgetEnabled || reward.isRedeemableOnline);
  const rewardsActivity = rewards?.rewardsActivity;
  const isLoyaltyMember = rewards?.isLoyaltyMember ?? false;
  const isPoints = loyaltyConfig?.tierEarningBasis === TierEarningBasis.points;
  const isDiscountRedemption = loyaltyConfig?.redemptionType?.toLowerCase() === RedemptionType.discount;
  const filteredRewardsActivity = rewardsActivity
    ?.filter((rewardActivity) => rewardActivity !== null)
    .map((rewardData) => removeTypename(rewardData));

  // Tiers calculations for single tier or multiple tiers (coerce for number/string from API)
  const rewardsTierId = rewards?.tierId != null ? Number(rewards.tierId) : undefined;
  const currentTierIndex =
    loyaltyConfig?.tiers?.findIndex((tier) => tier?.id != null && Number(tier.id) === rewardsTierId) ?? -1;
  const hasMatchingTier = currentTierIndex >= 0;
  const currentTier = hasMatchingTier ? loyaltyConfig?.tiers?.[currentTierIndex] : undefined;
  const nextTier = hasMatchingTier ? loyaltyConfig?.tiers?.[currentTierIndex + 1] : loyaltyConfig?.tiers?.[0];
  const { onlineName } = loyaltyConfig ?? {};
  const { badgeColor, threshold: currentTierThreshold = 0, name: currentTierName } = currentTier ?? {};
  const { name: nextTierName, threshold: nextTierThreshold } = nextTier ?? {};

  const activeTierAccrualRate = currentTier?.accrualRate ?? null;
  const envelopeAccrualRate = loyaltyConfig?.accrualRate ?? null;
  const accrualRate = activeTierAccrualRate ?? envelopeAccrualRate;

  const currentBalance = Number(rewards?.balance ?? 0);
  const currentYearTierBalance = Number(rewards?.currentYearTierBalance ?? 0);
  const expirationDate = rewards?.expirationDate;
  // Expiry display requires both the rollout flag and a mode that actually expires points.
  // Mode 2 = ExpireFromDateGranted (per-grant), mode 3 = ExpireAllUnredeemed (balance marker).
  // Gating on mode here ensures stale grant data from a previous mode never leaks through.
  const isExpirationDisplayEnabled =
    isLoyaltySoonestExpiringBalanceEnabled &&
    (loyaltyConfig?.loyaltyExpirationMode === LoyaltyExpirationMode.expireFromDateGranted ||
      loyaltyConfig?.loyaltyExpirationMode === LoyaltyExpirationMode.expireAllUnredeemed);
  const soonestExpiringBalance = isExpirationDisplayEnabled ? rewards?.soonestExpiringBalance ?? null : null;
  const soonestExpirationDate = isExpirationDisplayEnabled ? rewards?.soonestExpirationDate ?? null : null;

  // When user has not met any tier yet, progress is from 0 to first tier threshold
  const effectiveCurrentTierThreshold = hasMatchingTier ? currentTierThreshold : 0;

  // Handle single tier vs multiple tiers logic
  const isSingleTier = loyaltyConfig?.tiers?.length === 1;
  const singleTierThreshold = loyaltyConfig?.tiers?.[0]?.threshold ?? 0;
  const targetBalance = isSingleTier ? singleTierThreshold : nextTierThreshold ?? 0;
  const progressToNextTier = isSingleTier ? singleTierThreshold : targetBalance - effectiveCurrentTierThreshold;
  const currentProgress = isSingleTier
    ? currentYearTierBalance
    : currentYearTierBalance - effectiveCurrentTierThreshold;
  const progressPercentageToNextTier =
    progressToNextTier === 0 ? '0%' : `${Math.min((currentProgress / progressToNextTier) * 100, 100)}%`;

  // Rewards calculations
  const rewardslist =
    rewards?.rewards.filter((reward) => reward?.available).sort((a, b) => (a?.costV2 ?? 0) - (b?.costV2 ?? 0)) ?? [];
  const nextReward = rewardslist.find((reward) => currentBalance < (reward?.costV2 ?? 0));
  const targetRewardsBalance = nextReward?.value ? Number(nextReward.costV2) : 0;
  const progressToNextReward = targetRewardsBalance - currentBalance;
  // Progress toward next reward = current balance vs next reward cost (points/dollars needed)
  const progressPercentageToNextReward =
    progressToNextReward === 0 || targetRewardsBalance === 0
      ? '0%'
      : `${Math.min((currentBalance / targetRewardsBalance) * 100, 100)}%`;

  const balanceDaysToExpiration = expirationDate
    ? Math.ceil((new Date(expirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const formatValueForDisplay = (value: number, label: string, isLoyaltyPoints: boolean): string => {
    if (isLoyaltyPoints) {
      return `${String(parseFloat(value.toFixed(2)))} ${label}`;
    }

    return currencyFormatter.format(value);
  };

  // Subject-verb agreement: plural point balances take "expire", a single point
  // or any cash amount (grammatically singular) takes "expires".
  const getExpiresVerb = (value: number): string => (isDiscountRedemption && value !== 1 ? 'expire' : 'expires');

  // Matching unit agreement for point balances ("1 pt" vs "5 pts"). Cash formats
  // as currency, so its label is unused.
  const getExpiryUnit = (value: number): string => {
    if (!isDiscountRedemption) {
      return 'cash';
    }
    return value === 1 ? 'pt' : 'pts';
  };

  const formatValueForDaysForExpire = (daysToExpiration: number): string => {
    if (!expirationDate) {
      return isDiscountRedemption ? 'Rewards points do not expire' : 'Rewards do not expire';
    }

    // "X days from last transaction" mode: always show the specific expiry date regardless of flag.
    // "X days from date granted" mode with flag ON is handled via showSoonestExpiry upstream —
    // this path is only reached when soonestExpirationDate is unavailable.
    const tz = dispensary?.timezone ?? 'UTC';
    const formattedDate = formatLoyaltyExpirationDate(expirationDate, tz);
    if (daysToExpiration > 0) {
      return `Expires ${formattedDate}`;
    }
    const expLocal = moment.tz(toUtcString(expirationDate), tz);
    const nowLocal = moment.tz(tz);
    if (expLocal.isSame(nowLocal, 'day')) {
      return isDiscountRedemption ? 'Rewards points expire today' : 'Rewards expire today';
    }
    return isDiscountRedemption ? 'Rewards points have expired' : 'Rewards have expired';
  };

  const shouldShowLoyaltyWidget = getShouldShowLoyaltyWidget({
    loyaltyConfig,
    isLoyaltyMember,
  });

  // viewDiscountRewards is view only when true
  const showDiscountRewardsOnly = isDutchiePOSLoyaltyEnabled ? !!loyaltyConfig?.viewDiscountRewards : false;

  // showDiscountRewardsOnly depends only on isDutchiePOSLoyaltyEnabled (from dispensary data, available
  // immediately) and the GraphQL-loaded loyaltyConfig, so it can never be a stale false while data is still
  // in flight. loyaltyConfigLoading and rewardsLoading are kept below purely so the button fails closed while
  // that data is loading, independent of showDiscountRewardsOnly.
  const isRewardApplyBlocked = loyaltyConfigLoading || rewardsLoading || showDiscountRewardsOnly;

  // Alpine/SpringBig always allow the checkout rewards block. Otherwise DPOS requires loaded config and
  // onlineRedemptionEnabledOnWeb.
  const showRewardsBlockOnWeb = getShowRewardsBlockOnWeb({
    isAlpineIQEnabled,
    isSpringbigEnabled,
    isDutchiePOSLoyaltyEnabled,
    loyaltyConfig,
  });

  return {
    loyaltyConfig,
    loading: loyaltyConfigLoading || rewardsLoading,
    formatValueForDisplay,
    formatExpirationDate: (isoDate: string) => formatLoyaltyExpirationDate(isoDate, dispensary?.timezone ?? 'UTC'),
    rewardsProgramName: onlineName ?? 'Rewards',
    balanceDaysToExpiration: balanceDaysToExpiration && balanceDaysToExpiration < 0 ? 0 : balanceDaysToExpiration,
    badgeColor: badgeColor ?? '#ffffff',
    currentTierName,
    currentTierThreshold,
    nextTierName,
    currentBalance,
    currentYearTierBalance,
    targetBalance,
    progressPercentageToNextTier,
    progressPercentageToNextReward,
    progressToNextReward,
    targetRewardsBalance,
    currentRewardsBalance: currentBalance,
    isLoyaltyMember,
    isPoints,
    redemptionType: loyaltyConfig?.redemptionType ?? '',
    hasTiers: (loyaltyConfig?.tiers?.length ?? 0) > 0,
    isSingleTier,
    isDiscountRedemption,
    posCustomerId: rewards?.posCustomerId ?? '',
    filteredRewardsActivity,
    formatValueForDaysForExpire,
    getExpiresVerb,
    getExpiryUnit,
    expirationDate: expirationDate ?? '',
    soonestExpiringBalance,
    soonestExpirationDate,
    loyaltyWidgetEnabled: shouldShowLoyaltyWidget ?? false,
    rewardsArray: rewardsArray ?? [],
    showDiscountRewardsOnly,
    isRewardApplyBlocked,
    showRewardsBlockOnWeb,
    accrualRate,
    isExpirationDisplayEnabled,
  };
};
